import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = '/api';
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

interface Props {
  user: any;
  tenants?: { id: number; company_name?: string }[];
}

interface PickupGroup {
  id: number;
  name: string;
  description?: string;
  tenant_id: number;
  pickup_group_number?: number;
  member_count?: number;
}

interface GroupMember {
  extension: string;
  display_name?: string;
  blf_state?: 'available' | 'ringing' | 'in_call' | 'unavailable';
  pickup_group_number?: number;
}

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error' | 'warning';
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const s = {
  page: {
    background: '#f8fafc',
    minHeight: '100vh',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    color: '#1e293b',
  } as React.CSSProperties,

  tenantBar: {
    background: '#2563eb',
    color: '#fff',
    padding: '10px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    fontSize: 14,
    fontWeight: 500,
  } as React.CSSProperties,

  tenantSelect: {
    background: 'rgba(255,255,255,0.15)',
    border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: 6,
    color: '#fff',
    padding: '4px 10px',
    fontSize: 14,
    cursor: 'pointer',
    outline: 'none',
  } as React.CSSProperties,

  layout: {
    display: 'flex',
    gap: 0,
    height: 'calc(100vh - 52px)',
    overflow: 'hidden',
  } as React.CSSProperties,

  leftPanel: {
    width: 340,
    borderRight: '1px solid #e5e7eb',
    display: 'flex',
    flexDirection: 'column' as const,
    background: '#fff',
    flexShrink: 0,
  } as React.CSSProperties,

  leftHeader: {
    padding: '18px 16px 12px',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  } as React.CSSProperties,

  leftTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#1e293b',
    margin: 0,
  } as React.CSSProperties,

  btnPrimary: {
    background: '#6366f1',
    color: '#fff',
    border: 'none',
    borderRadius: 7,
    padding: '7px 14px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    transition: 'background 0.15s',
  } as React.CSSProperties,

  btnDanger: {
    background: 'none',
    border: '1px solid #ef4444',
    color: '#ef4444',
    borderRadius: 6,
    padding: '4px 10px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.15s',
  } as React.CSSProperties,

  btnGhost: {
    background: 'none',
    border: '1px solid #6366f1',
    color: '#6366f1',
    borderRadius: 6,
    padding: '4px 10px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
  } as React.CSSProperties,

  btnSync: {
    background: '#0ea5e9',
    color: '#fff',
    border: 'none',
    borderRadius: 7,
    padding: '7px 14px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
  } as React.CSSProperties,

  groupList: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: '10px 12px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8,
  } as React.CSSProperties,

  groupCard: (selected: boolean): React.CSSProperties => ({
    border: `1.5px solid ${selected ? '#6366f1' : '#e5e7eb'}`,
    borderRadius: 10,
    padding: '12px 14px',
    cursor: 'pointer',
    background: selected ? '#f5f3ff' : '#fff',
    transition: 'border-color 0.15s, background 0.15s',
    boxShadow: selected ? '0 0 0 2px #a5b4fc44' : 'none',
  }),

  groupName: {
    fontWeight: 700,
    fontSize: 14,
    color: '#1e293b',
    marginBottom: 2,
  } as React.CSSProperties,

  groupDesc: {
    fontSize: 12,
    color: '#64748b',
    marginBottom: 8,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  } as React.CSSProperties,

  groupMeta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  } as React.CSSProperties,

  memberCount: {
    fontSize: 12,
    color: '#64748b',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  } as React.CSSProperties,

  groupActions: {
    display: 'flex',
    gap: 6,
  } as React.CSSProperties,

  // Right panel
  rightPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    background: '#f8fafc',
    overflow: 'hidden',
  } as React.CSSProperties,

  rightHeader: {
    padding: '16px 24px 12px',
    borderBottom: '1px solid #e5e7eb',
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  } as React.CSSProperties,

  rightTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#1e293b',
    margin: 0,
  } as React.CSSProperties,

  syncNote: {
    fontSize: 12,
    color: '#64748b',
    marginTop: 6,
  } as React.CSSProperties,

  rightBody: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: 20,
  } as React.CSSProperties,

  addMemberCard: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: '14px 16px',
    marginBottom: 16,
    display: 'flex',
    gap: 10,
    alignItems: 'flex-end',
  } as React.CSSProperties,

  formGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 4,
  } as React.CSSProperties,

  label: {
    fontSize: 12,
    fontWeight: 600,
    color: '#64748b',
  } as React.CSSProperties,

  input: {
    border: '1px solid #e5e7eb',
    borderRadius: 6,
    padding: '6px 10px',
    fontSize: 13,
    color: '#1e293b',
    outline: 'none',
    background: '#fff',
    width: 130,
    transition: 'border-color 0.15s',
  } as React.CSSProperties,

  memberCard: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  } as React.CSSProperties,

  memberLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  } as React.CSSProperties,

  memberName: {
    fontSize: 14,
    fontWeight: 600,
    color: '#1e293b',
  } as React.CSSProperties,

  memberExt: {
    fontSize: 12,
    color: '#64748b',
    marginTop: 1,
  } as React.CSSProperties,

  memberRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  } as React.CSSProperties,

  pgBadge: {
    background: '#ede9fe',
    color: '#6366f1',
    borderRadius: 5,
    padding: '2px 8px',
    fontSize: 11,
    fontWeight: 700,
  } as React.CSSProperties,

  btnRemove: {
    background: 'none',
    border: 'none',
    color: '#ef4444',
    cursor: 'pointer',
    fontSize: 18,
    lineHeight: '1',
    padding: '2px 6px',
    borderRadius: 4,
    fontWeight: 700,
    transition: 'background 0.12s',
  } as React.CSSProperties,

  // Empty states
  emptyState: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    color: '#64748b',
    textAlign: 'center' as const,
    gap: 10,
  } as React.CSSProperties,

  emptyEmoji: {
    fontSize: 48,
    marginBottom: 4,
  } as React.CSSProperties,

  emptyTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: '#1e293b',
    margin: 0,
  } as React.CSSProperties,

  emptyText: {
    fontSize: 13,
    color: '#64748b',
    margin: 0,
    maxWidth: 240,
  } as React.CSSProperties,

  // Skeleton
  skeleton: (w?: string | number, h?: number): React.CSSProperties => ({
    background: 'linear-gradient(90deg, #e5e7eb 0%, #f1f5f9 50%, #e5e7eb 100%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.4s infinite',
    borderRadius: 6,
    height: h ?? 14,
    width: w ?? '100%',
  }),

  // Modal
  modalOverlay: {
    position: 'fixed' as const,
    inset: 0,
    background: 'rgba(0,0,0,0.35)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as React.CSSProperties,

  modal: {
    background: '#fff',
    borderRadius: 14,
    padding: '28px 28px 24px',
    width: 420,
    maxWidth: '95vw',
    boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
  } as React.CSSProperties,

  modalTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: '#1e293b',
    margin: '0 0 20px',
  } as React.CSSProperties,

  modalField: {
    marginBottom: 16,
  } as React.CSSProperties,

  modalLabel: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#64748b',
    marginBottom: 5,
  } as React.CSSProperties,

  modalInput: {
    width: '100%',
    border: '1px solid #e5e7eb',
    borderRadius: 7,
    padding: '8px 12px',
    fontSize: 14,
    color: '#1e293b',
    outline: 'none',
    boxSizing: 'border-box' as const,
    transition: 'border-color 0.15s',
  } as React.CSSProperties,

  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 24,
  } as React.CSSProperties,

  btnSecondary: {
    background: 'none',
    border: '1px solid #e5e7eb',
    borderRadius: 7,
    padding: '7px 16px',
    fontSize: 13,
    fontWeight: 600,
    color: '#64748b',
    cursor: 'pointer',
  } as React.CSSProperties,

  // Toast
  toastContainer: {
    position: 'fixed' as const,
    top: 20,
    right: 20,
    zIndex: 2000,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 8,
    pointerEvents: 'none' as const,
  } as React.CSSProperties,

  toast: (type: string): React.CSSProperties => ({
    background: type === 'success' ? '#22c55e' : type === 'error' ? '#ef4444' : '#f59e0b',
    color: '#fff',
    borderRadius: 9,
    padding: '10px 18px',
    fontSize: 13,
    fontWeight: 600,
    boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
    pointerEvents: 'auto' as const,
    maxWidth: 320,
    animation: 'slideInRight 0.2s ease',
  }),
};

// ─── BLF State Badge ──────────────────────────────────────────────────────────

function BlfBadge({ state, pulse }: { state?: string; pulse: boolean }) {
  const cfg: Record<string, { bg: string; color: string; label: string }> = {
    available:   { bg: '#dcfce7', color: '#15803d', label: 'Available' },
    ringing:     { bg: '#fef9c3', color: '#92400e', label: 'Ringing' },
    in_call:     { bg: '#fee2e2', color: '#b91c1c', label: 'In Call' },
    unavailable: { bg: '#f1f5f9', color: '#64748b', label: 'Unavailable' },
  };
  const c = cfg[state ?? 'unavailable'] ?? cfg.unavailable;

  const dotStyle: React.CSSProperties = {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: c.color,
    flexShrink: 0,
    ...(state === 'ringing' && pulse
      ? { boxShadow: `0 0 0 4px ${c.color}44` }
      : state === 'ringing'
      ? { boxShadow: `0 0 0 1px ${c.color}22` }
      : {}),
    transition: 'box-shadow 0.4s ease',
  };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: c.bg, color: c.color, borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>
      <span style={dotStyle} />
      {c.label}
    </span>
  );
}

// ─── Skeleton rows ────────────────────────────────────────────────────────────

function GroupSkeleton() {
  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px', background: '#fff' }}>
      <div style={s.skeleton('60%', 14)} />
      <div style={{ marginTop: 6, ...s.skeleton('85%', 11) }} />
      <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between' }}>
        <div style={s.skeleton(60, 11)} />
        <div style={{ display: 'flex', gap: 6 }}>
          <div style={s.skeleton(60, 24)} />
          <div style={s.skeleton(50, 24)} />
        </div>
      </div>
    </div>
  );
}

function MemberSkeleton() {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 16px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', ...s.skeleton(36, 36) }} />
      <div style={{ flex: 1 }}>
        <div style={s.skeleton('40%', 13)} />
        <div style={{ marginTop: 4, ...s.skeleton('25%', 11) }} />
      </div>
      <div style={s.skeleton(70, 20)} />
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function PickupGroupsAdmin({ user, tenants }: Props) {
  const isSuperAdmin = !!(user?.role === 'super_admin' || user?.is_admin);

  const [selectedTenantId, setSelectedTenantId] = useState<number>(
    isSuperAdmin ? (tenants?.[0]?.id ?? user?.tenant_id ?? 1) : (user?.tenant_id ?? 1)
  );

  const [groups, setGroups] = useState<PickupGroup[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<PickupGroup | null>(null);

  const [members, setMembers] = useState<GroupMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<PickupGroup | null>(null);
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formSaving, setFormSaving] = useState(false);

  const [addExt, setAddExt] = useState('');
  const [addName, setAddName] = useState('');
  const [addingMember, setAddingMember] = useState(false);

  // All extensions for selected tenant — used to populate the add-member picker
  const [tenantExtensions, setTenantExtensions] = useState<{ extension_number: string; display_name: string; username: string }[]>([]);
  const [extSearch, setExtSearch] = useState('');
  const [showExtDropdown, setShowExtDropdown] = useState(false);

  const [syncing, setSyncing] = useState(false);

  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastId = useRef(0);

  // BLF pulse timer
  const [blfPulse, setBlfPulse] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setBlfPulse(p => !p), 500);
    return () => clearInterval(t);
  }, []);

  // Inject global keyframes once
  useEffect(() => {
    if (document.getElementById('ce-pickup-styles')) return;
    const style = document.createElement('style');
    style.id = 'ce-pickup-styles';
    style.textContent = `
      @keyframes shimmer {
        0%   { background-position: -200% 0; }
        100% { background-position:  200% 0; }
      }
      @keyframes slideInRight {
        from { opacity: 0; transform: translateX(40px); }
        to   { opacity: 1; transform: translateX(0); }
      }
    `;
    document.head.appendChild(style);
  }, []);

  const pushToast = useCallback((message: string, type: Toast['type'] = 'success') => {
    const id = ++toastId.current;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3200);
  }, []);

  // ── Load groups ──────────────────────────────────────────────────────────────

  const loadGroups = useCallback(async (tenantId: number) => {
    if (tenantId == null || tenantId === 0) return;
    setLoadingGroups(true);
    setSelectedGroup(null);
    setMembers([]);
    try {
      const res = await axios.get(`${API}/pickup-groups/?tenant_id=${tenantId}`, authHeaders());
      setGroups(Array.isArray(res.data) ? res.data : (res.data?.groups ?? []))
    } catch (e: any) {
      pushToast(e?.response?.data?.message ?? 'Failed to load groups', 'error');
    } finally {
      setLoadingGroups(false);
    }
  }, [pushToast]);

  useEffect(() => {
    loadGroups(selectedTenantId);
  }, [selectedTenantId, loadGroups]);

  // Load all extensions for this tenant whenever tenant changes
  useEffect(() => {
    if (!selectedTenantId) return;
    const tid = selectedTenantId;
    axios.get(`${API}/extensions`, {
      ...authHeaders(),
      params: { tenant_id: tid },
    }).then(r => {
      // /api/extensions returns a bare array
      const exts = Array.isArray(r.data) ? r.data : [];
      setTenantExtensions(exts);
    }).catch(() => setTenantExtensions([]));
  }, [selectedTenantId]);

  // ── Load members ─────────────────────────────────────────────────────────────

  const loadMembers = useCallback(async (group: PickupGroup) => {
    setSelectedGroup(group);
    setMembers([]);
    setLoadingMembers(true);
    try {
      const res = await axios.get(`${API}/pickup-groups/${group.id}/members?tenant_id=${group.tenant_id}`, authHeaders());
      setMembers(Array.isArray(res.data) ? res.data : (res.data?.members ?? []))
    } catch (e: any) {
      pushToast(e?.response?.data?.message ?? 'Failed to load members', 'error');
    } finally {
      setLoadingMembers(false);
    }
  }, [pushToast]);

  // ── Create / Edit group ──────────────────────────────────────────────────────

  const openCreate = () => {
    setEditingGroup(null);
    setFormName('');
    setFormDesc('');
    setShowModal(true);
  };

  const openEdit = (g: PickupGroup, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingGroup(g);
    setFormName(g.name);
    setFormDesc(g.description ?? '');
    setShowModal(true);
  };

  const closeModal = () => {
    if (formSaving) return;
    setShowModal(false);
  };

  const saveGroup = async () => {
    if (!formName.trim()) { pushToast('Group name is required', 'warning'); return; }
    setFormSaving(true);
    try {
      if (editingGroup) {
        await axios.put(`${API}/pickup-groups/${editingGroup.id}`, { name: formName.trim(), description: formDesc.trim() }, authHeaders());
        pushToast('Group updated');
      } else {
        await axios.post(`${API}/pickup-groups/`, { name: formName.trim(), description: formDesc.trim(), tenant_id: selectedTenantId }, authHeaders());
        pushToast('Group created');
      }
      setShowModal(false);
      await loadGroups(selectedTenantId);
    } catch (e: any) {
      pushToast(e?.response?.data?.message ?? 'Save failed', 'error');
    } finally {
      setFormSaving(false);
    }
  };

  // ── Delete group ─────────────────────────────────────────────────────────────

  const deleteGroup = async (g: PickupGroup, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Delete group "${g.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/pickup-groups/${g.id}`, authHeaders());
      pushToast('Group deleted');
      if (selectedGroup?.id === g.id) { setSelectedGroup(null); setMembers([]); }
      await loadGroups(selectedTenantId);
    } catch (e: any) {
      pushToast(e?.response?.data?.message ?? 'Delete failed', 'error');
    }
  };

  // ── Add member ───────────────────────────────────────────────────────────────

  const addMember = async () => {
    if (!selectedGroup) return;
    if (!addExt.trim()) { pushToast('Extension is required', 'warning'); return; }
    setAddingMember(true);
    try {
      await axios.post(`${API}/pickup-groups/${selectedGroup.id}/members`, { extension: addExt.trim(), display_name: addName.trim() || undefined }, authHeaders());
      setAddExt('');
      setAddName('');
      setExtSearch('');
      pushToast('Member added');
      await loadMembers(selectedGroup);
    } catch (e: any) {
      pushToast(e?.response?.data?.message ?? 'Failed to add member', 'error');
    } finally {
      setAddingMember(false);
    }
  };

  // ── Remove member ─────────────────────────────────────────────────────────────

  const removeMember = async (extension: string) => {
    if (!selectedGroup) return;
    if (!window.confirm(`Remove extension ${extension} from group?`)) return;
    try {
      await axios.delete(`${API}/pickup-groups/${selectedGroup.id}/members/${extension}`, authHeaders());
      pushToast('Member removed');
      setMembers(prev => prev.filter(m => m.extension !== extension));
    } catch (e: any) {
      pushToast(e?.response?.data?.message ?? 'Failed to remove member', 'error');
    }
  };

  // ── Sync ──────────────────────────────────────────────────────────────────────

  const syncAsterisk = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/pickup-groups/sync-asterisk?tenant_id=${selectedTenantId}`, {}, authHeaders());
      pushToast('Telephony config synced successfully');
    } catch (e: any) {
      pushToast(e?.response?.data?.message ?? 'Sync failed', 'error');
    } finally {
      setSyncing(false);
    }
  };

  // ── Avatar helper ─────────────────────────────────────────────────────────────

  const avatarStyle = (color: string): React.CSSProperties => ({
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: color,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    fontWeight: 700,
    color: '#fff',
    flexShrink: 0,
  });

  const avatarColors = ['#6366f1', '#0ea5e9', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899'];
  const getColor = (str: string) => avatarColors[str.charCodeAt(0) % avatarColors.length];

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div style={s.page}>
      {/* Tenant selector bar — super_admin only */}
      {isSuperAdmin && tenants && tenants.length > 0 && (
        <div style={s.tenantBar}>
          <span>Tenant:</span>
          <select
            style={s.tenantSelect}
            value={selectedTenantId}
            onChange={e => setSelectedTenantId(Number(e.target.value))}
          >
            {tenants.map(t => (
              <option key={t.id} value={t.id}>
                {t.company_name ?? `Tenant #${t.id}`}
              </option>
            ))}
          </select>
          <span style={{ opacity: 0.7, fontSize: 12 }}>Managing pickup groups for selected tenant</span>
        </div>
      )}

      <div style={s.layout}>
        {/* ── Left Panel ── */}
        <div style={s.leftPanel}>
          <div style={s.leftHeader}>
            <h2 style={s.leftTitle}>Pickup Groups</h2>
            <button style={s.btnPrimary} onClick={openCreate}>
              + New Group
            </button>
          </div>

          <div style={s.groupList}>
            {loadingGroups ? (
              [1, 2, 3].map(i => <GroupSkeleton key={i} />)
            ) : groups.length === 0 ? (
              <div style={s.emptyState}>
                <span style={s.emptyEmoji}>📞</span>
                <p style={s.emptyTitle}>No pickup groups</p>
                <p style={s.emptyText}>Create a group so agents can intercept each other's calls.</p>
              </div>
            ) : (
              groups.map(g => (
                <div
                  key={g.id}
                  style={s.groupCard(selectedGroup?.id === g.id)}
                  onClick={() => loadMembers(g)}
                >
                  <div style={s.groupName}>{g.name}</div>
                  {g.description && <div style={s.groupDesc}>{g.description}</div>}
                  <div style={s.groupMeta}>
                    <span style={s.memberCount}>
                      <span>👥</span>
                      {g.member_count ?? 0} member{(g.member_count ?? 0) !== 1 ? 's' : ''}
                    </span>
                    <div style={s.groupActions}>
                      <button
                        style={s.btnGhost}
                        onClick={e => { e.stopPropagation(); loadMembers(g); }}
                      >
                        Edit Members
                      </button>
                      <button style={s.btnGhost} onClick={e => openEdit(g, e)} title="Edit group info">
                        ✏️
                      </button>
                      <button style={s.btnDanger} onClick={e => deleteGroup(g, e)}>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* ── Right Panel ── */}
        <div style={s.rightPanel}>
          {selectedGroup ? (
            <>
              <div style={s.rightHeader}>
                <div>
                  <h2 style={s.rightTitle}>
                    Members — {selectedGroup.name}
                  </h2>
                  {selectedGroup.description && (
                    <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{selectedGroup.description}</div>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                  <button style={s.btnSync} onClick={syncAsterisk} disabled={syncing}>
                    {syncing ? '⟳ Syncing…' : '⚡ Sync Telephony Config'}
                  </button>
                  <span style={s.syncNote}>
                    Sync applies callgroup/pickupgroup to all SIP endpoints and reloads PJSIP.&nbsp;
                    Agents can dial <strong>*9</strong> (group pickup) or <strong>*88EXTEN</strong> (directed pickup).
                  </span>
                </div>
              </div>

              <div style={s.rightBody}>
                {/* Add member form — searchable extension picker */}
                <div style={s.addMemberCard}>
                  <div style={{ ...s.formGroup, position: 'relative' as const, flex: 2 }}>
                    <label style={s.label}>Select Extension *</label>
                    <input
                      style={{ ...s.input, cursor: 'pointer' }}
                      placeholder="Search by name, extension or username…"
                      value={extSearch}
                      onChange={e => {
                        setExtSearch(e.target.value);
                        setAddExt('');
                        setAddName('');
                        setShowExtDropdown(true);
                      }}
                      onFocus={() => setShowExtDropdown(true)}
                      onBlur={() => setTimeout(() => setShowExtDropdown(false), 180)}
                      autoComplete="off"
                    />
                    {showExtDropdown && (
                      <div style={{
                        position: 'absolute' as const, top: '100%', left: 0, right: 0, zIndex: 50,
                        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
                        boxShadow: '0 8px 24px rgba(0,0,0,.12)', maxHeight: 240, overflowY: 'auto' as const,
                        marginTop: 2,
                      }}>
                        {tenantExtensions
                          .filter(ex => {
                            if (!extSearch) return true;
                            const q = extSearch.toLowerCase();
                            return ex.extension_number.includes(q) ||
                              (ex.display_name || '').toLowerCase().includes(q) ||
                              (ex.username || '').toLowerCase().includes(q);
                          })
                          .filter(ex => !members.some(m => m.extension === ex.extension_number))
                          .map(ex => (
                            <div
                              key={ex.extension_number}
                              onMouseDown={() => {
                                setAddExt(ex.extension_number);
                                setAddName(ex.display_name || ex.username || '');
                                setExtSearch(`${ex.extension_number} — ${ex.display_name || ex.username || ''}`);
                                setShowExtDropdown(false);
                              }}
                              style={{
                                padding: '9px 14px', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: 10,
                                borderBottom: '1px solid #f1f5f9',
                                transition: 'background .1s',
                              }}
                              onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                              onMouseLeave={e => (e.currentTarget.style.background = '')}
                            >
                              <span style={{
                                width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                                background: '#ede9fe', color: '#6d28d9', fontWeight: 700,
                                fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>
                                {ex.extension_number}
                              </span>
                              <div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                                  {ex.display_name || ex.username || ex.extension_number}
                                </div>
                                <div style={{ fontSize: 11, color: '#64748b' }}>
                                  Ext {ex.extension_number}{ex.username ? ` · @${ex.username}` : ''}
                                </div>
                              </div>
                            </div>
                          ))}
                        {tenantExtensions.filter(ex => {
                          if (!extSearch) return true;
                          const q = extSearch.toLowerCase();
                          return ex.extension_number.includes(q) ||
                            (ex.display_name || '').toLowerCase().includes(q) ||
                            (ex.username || '').toLowerCase().includes(q);
                        }).filter(ex => !members.some(m => m.extension === ex.extension_number)).length === 0 && (
                          <div style={{ padding: '12px 14px', color: '#94a3b8', fontSize: 13, textAlign: 'center' as const }}>
                            {members.length === tenantExtensions.length ? 'All extensions are already in this group' : 'No extensions match your search'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{ ...s.formGroup, flex: 1 }}>
                    <label style={s.label}>Display Name</label>
                    <input
                      style={s.input}
                      placeholder="Auto-filled"
                      value={addName}
                      onChange={e => setAddName(e.target.value)}
                    />
                  </div>
                  <button
                    style={{ ...s.btnPrimary, height: 34, alignSelf: 'flex-end', whiteSpace: 'nowrap' as const }}
                    onClick={addMember}
                    disabled={addingMember || !addExt}
                  >
                    {addingMember ? 'Adding…' : '+ Add Member'}
                  </button>
                </div>

                {/* Member list */}
                {loadingMembers ? (
                  [1, 2, 3, 4].map(i => <MemberSkeleton key={i} />)
                ) : members.length === 0 ? (
                  <div style={s.emptyState}>
                    <span style={s.emptyEmoji}>🧑‍💼</span>
                    <p style={s.emptyTitle}>No members yet</p>
                    <p style={s.emptyText}>Add extensions above so they can pick up each other's calls.</p>
                  </div>
                ) : (
                  members.map(m => {
                    const initials = m.display_name
                      ? m.display_name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
                      : m.extension.slice(0, 2);

                    return (
                      <div key={m.extension} style={s.memberCard}>
                        <div style={s.memberLeft}>
                          <div style={avatarStyle(getColor(m.extension))}>
                            {initials}
                          </div>
                          <div>
                            <div style={s.memberName}>{m.display_name || m.extension}</div>
                            {m.display_name && <div style={s.memberExt}>Ext {m.extension}</div>}
                          </div>
                        </div>
                        <div style={s.memberRight}>
                          <BlfBadge state={m.blf_state} pulse={blfPulse} />
                          {m.pickup_group_number != null && (
                            <span style={s.pgBadge}>Asterisk PG #{m.pickup_group_number}</span>
                          )}
                          <button
                            style={s.btnRemove}
                            onClick={() => removeMember(m.extension)}
                            title="Remove from group"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          ) : (
            <div style={{ ...s.emptyState, flex: 1 }}>
              <span style={s.emptyEmoji}>👈</span>
              <p style={s.emptyTitle}>Select a group</p>
              <p style={s.emptyText}>Click "Edit Members" on a group to manage its extensions and BLF states.</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Create / Edit Modal ── */}
      {showModal && (
        <div style={s.modalOverlay}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h3 style={s.modalTitle}>{editingGroup ? 'Edit Group' : 'New Pickup Group'}</h3>

            <div style={s.modalField}>
              <label style={s.modalLabel}>Group Name *</label>
              <input
                style={s.modalInput}
                placeholder="e.g. Sales Floor"
                value={formName}
                autoFocus
                onChange={e => setFormName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && saveGroup()}
              />
            </div>

            <div style={s.modalField}>
              <label style={s.modalLabel}>Description</label>
              <input
                style={s.modalInput}
                placeholder="Optional description"
                value={formDesc}
                onChange={e => setFormDesc(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && saveGroup()}
              />
            </div>

            {!editingGroup && (
              <div style={{ fontSize: 12, color: '#64748b', background: '#f1f5f9', borderRadius: 7, padding: '8px 12px' }}>
                A pickup group number will be auto-assigned when you sync.
              </div>
            )}

            <div style={s.modalActions}>
              <button style={s.btnSecondary} onClick={closeModal} disabled={formSaving}>
                Cancel
              </button>
              <button style={s.btnPrimary} onClick={saveGroup} disabled={formSaving}>
                {formSaving ? 'Saving…' : editingGroup ? 'Save Changes' : 'Create Group'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast Container ── */}
      <div style={s.toastContainer}>
        {toasts.map(t => (
          <div key={t.id} style={s.toast(t.type)}>
            {t.type === 'success' ? '✓ ' : t.type === 'error' ? '✕ ' : '⚠ '}{t.message}
          </div>
        ))}
      </div>
    </div>
  );
}
